import React, { MouseEvent, ReactElement, forwardRef, useContext } from 'react';
import classNames from 'classnames';

import {
  Comp,
  EventKey,
  FocusWithinGroupActionType,
  FocusWithinGroupContext,
  isComponent,
  useTheme,
} from '@redsift/design-system';
import { MenuButtonTriggerProps } from './types';
import { useMergeRefs, usePopoverContext } from '@redsift/popovers';
import { MenuButtonContext } from '../menu-button/context';

const COMPONENT_NAME = 'MenuButtonTrigger';
const CLASSNAME = 'redsift-menu-button-trigger';

/**
 * The MenuButtonTrigger component.
 */
export const MenuButtonTrigger: Comp<MenuButtonTriggerProps, HTMLButtonElement> = forwardRef((props, ref) => {
  const { children } = props;

  const { getReferenceProps, isOpen, handleOpen, refs } = usePopoverContext();
  const childrenRef = (children as any).ref;
  const triggerRef = useMergeRefs([refs.setReference, ref, childrenRef]);

  const focusContext = useContext(FocusWithinGroupContext);
  const menuButtonContext = useContext(MenuButtonContext);
  const theme = useTheme();

  const renderedChildren = typeof children === 'function' ? children({ isOpen }) : children;

  const handleKeyDown = (event: KeyboardEvent) => {
    const code = event.code;

    if (code === 'Escape' || code === 'Tab') {
      if (isOpen) {
        handleOpen(false);
      }
    } else if (code === 'ArrowDown') {
      if (!isOpen) {
        handleOpen(true);
      }
      if (focusContext.state.selectedId !== null) {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.ArrowDown,
              ctrlKey: event.ctrlKey,
            },
          },
        });
      } else {
        focusContext.dispatch({
          type: FocusWithinGroupActionType.DELAY_ACTION,
          payload: {
            type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
            payload: {
              key: EventKey.Home,
              ctrlKey: event.ctrlKey,
            },
          },
        });
      }
      event.preventDefault();
      event.stopPropagation();
    } else if (code === 'ArrowUp') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: focusContext.state.selectedId !== null ? EventKey.ArrowUp : EventKey.Home,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
      event.stopPropagation();
    } else if (code === 'Home') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.Home,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
      event.stopPropagation();
    } else if (code === 'End') {
      if (!isOpen) {
        handleOpen(true);
      }
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.KEY_DOWN_ON_LIST,
          payload: {
            key: EventKey.End,
            ctrlKey: event.ctrlKey,
          },
        },
      });
      event.preventDefault();
      event.stopPropagation();
    } else if (code === 'Enter') {
      if (isOpen && focusContext.state.selectedId) {
        document.getElementById(focusContext.state.selectedId)?.click();
        document.getElementById(focusContext.state.selectedId)?.getElementsByTagName('a')?.[0]?.click();
      }
    }
  };

  const handleClick = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isOpen) {
      handleOpen(false);
    } else {
      handleOpen(true);
      focusContext.dispatch({
        type: FocusWithinGroupActionType.DELAY_ACTION,
        payload: {
          type: FocusWithinGroupActionType.FOCUS_ON_LIST,
          payload: {
            id: focusContext.state.activedescendant?.[0] ?? '',
          },
        },
      });
    }
  };

  if (isComponent('Button')(renderedChildren) || isComponent('IconButton')(renderedChildren)) {
    return React.cloneElement(renderedChildren, {
      ...getReferenceProps({
        ref: triggerRef,
        ...props,
        ...(renderedChildren as ReactElement).props,
        children: (renderedChildren as ReactElement).props.children ?? '',
      }),
      theme,
      'aria-activedescendant': isOpen ? focusContext.state.activedescendant?.[0] : undefined,
      className: classNames((renderedChildren as ReactElement).props.className, menuButtonContext?.triggerClassName),
      color: menuButtonContext?.color ?? (renderedChildren as ReactElement).props.color,
      isActive: isOpen,
      isDisabled: menuButtonContext?.isDisabled,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      role: 'button',
      variant: menuButtonContext?.variant ?? (renderedChildren as ReactElement).props.variant,
    });
  }

  return <>{renderedChildren}</>;
});
MenuButtonTrigger.className = CLASSNAME;
MenuButtonTrigger.displayName = COMPONENT_NAME;
